import { isNotDefined } from '#misc/is-not-defined.guard';

/**
 * Implements a Least Recently Used (LRU) cache using a Map to store key-value pairs.
 * When the cache exceeds the specified maximum size, the least recently used item is evicted.
 *
 * @typeParam TKey - The type of the keys in the cache.
 * @typeParam TValue - The type of the values in the cache.
 */
export class LRUCache<TKey, TValue> {
  private map = new Map<TKey, TValue>();

  constructor(private max: number) {}

  /**
   * Clears all entries from the cache.
   */
  clear() {
    this.map.clear();
  }

  /**
   * Deletes an entry from the cache by its key.
   * @param key - The key of the entry to delete.
   */
  delete(key: TKey) {
    this.map.delete(key);
  }

  /**
   * Retrieves an entry from the cache by its key.
   * @param key - The key of the entry to retrieve.
   * @returns The value associated with the key, or undefined if not found.
   */
  get(key: TKey): TValue | undefined {
    const item = this.map.get(key);

    if (!item) {
      return undefined;
    }

    this.map.delete(key);
    this.map.set(key, item);

    return item;
  }

  /**
   * Adds or updates an entry in the cache.
   * @param key - The key of the entry to add or update.
   * @param value - The value to associate with the key.
   */
  set(key: TKey, value: TValue) {
    if (this.map.has(key)) {
      this.map.delete(key);
    } else if (this.map.size >= this.max) {
      const oldest = this.map.keys().next().value;

      if (isNotDefined(oldest)) {
        return;
      }

      this.map.delete(oldest);
    }

    this.map.set(key, value);
  }
}
